import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import SharedSlotsPreview from "@/components/SharedSlotsPreview";
import { CaptureMethodPicker } from "@/components/pano/CaptureMethod";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PROJECT_TYPES } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, Plus, X, FileStack, MapPin, Crosshair } from "lucide-react";

export default function ProjectCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer: "",
    project_name: "",
    site_number: "",
    site_name: "",
    market: "",
    address: "",
    latitude: null,
    longitude: null,
    foreman: "",
    crew_members: [],
    project_date: new Date().toISOString().slice(0, 10),
    project_type: "microwave",
    template_ids: [],
    capture_method: null,
  });
  // Both fields ("address" free-text + "coords" lat,lng combined input)
  // are always visible on the form now. Coordinates are required so
  // every project lands on the fleet map from Day 1; address is
  // optional supplemental data for humans.
  const [coordsInput, setCoordsInput] = useState("");
  const [coordsError, setCoordsError] = useState("");
  const [newCrew, setNewCrew] = useState("");
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get("/templates/photos").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  }, []);

  const toggleTemplate = (id) => {
    setForm((f) => ({
      ...f,
      template_ids: f.template_ids.includes(id)
        ? f.template_ids.filter((x) => x !== id)
        : [...f.template_ids, id],
    }));
  };

  const setField = (k, v) => setForm({ ...form, [k]: v });

  // Parse a "lat, lon" decimal-degrees string into form.latitude /
  // form.longitude. Accepts "35.123, -97.654", "35.123 -97.654", with or
  // without the comma. Rejects DMS notation (use a converter site first).
  // Sets coordsError on bad input and clears lat/lng so we don't ship
  // half-parsed data.
  const handleCoordsChange = (raw) => {
    setCoordsInput(raw);
    const trimmed = (raw || "").trim();
    if (!trimmed) {
      setCoordsError("");
      setForm((f) => ({ ...f, latitude: null, longitude: null }));
      return;
    }
    const parts = trimmed.split(/[,\s]+/).filter(Boolean);
    if (parts.length !== 2) {
      setCoordsError("Enter two numbers: latitude, longitude (decimal degrees).");
      setForm((f) => ({ ...f, latitude: null, longitude: null }));
      return;
    }
    const lat = Number(parts[0]);
    const lon = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      setCoordsError("Both values must be numbers in decimal degrees.");
      setForm((f) => ({ ...f, latitude: null, longitude: null }));
      return;
    }
    if (lat < -90 || lat > 90) {
      setCoordsError("Latitude must be between -90 and 90.");
      setForm((f) => ({ ...f, latitude: null, longitude: null }));
      return;
    }
    if (lon < -180 || lon > 180) {
      setCoordsError("Longitude must be between -180 and 180.");
      setForm((f) => ({ ...f, latitude: null, longitude: null }));
      return;
    }
    setCoordsError("");
    setForm((f) => ({ ...f, latitude: lat, longitude: lon }));
  };

  const addCrew = () => {
    if (!newCrew.trim()) return;
    setForm({ ...form, crew_members: [...form.crew_members, { name: newCrew.trim() }] });
    setNewCrew("");
  };
  const removeCrew = (i) => {
    setForm({ ...form, crew_members: form.crew_members.filter((_, idx) => idx !== i) });
  };

  // Safety-only projects exist purely to capture Tailgate + JSA — no
  // field photos are required, so we skip the photo-template gate.
  const isSafetyOnly = form.project_type === "safety_only";
  const isPano = form.project_type === "pano_site_audit";

  const submit = async (e) => {
    e.preventDefault();
    if (isPano && !form.capture_method) {
      toast.error("Pick Drone Flight or Tower Climbing for this Pano site audit");
      return;
    }
    if (!isSafetyOnly && !form.template_ids?.length) {
      toast.error("Select at least one photo template before creating the project");
      return;
    }
    // Coordinates are mandatory — every project needs to be plottable
    // on the fleet map and have a working Navigate button. Backend
    // enforces this too; this client-side check just surfaces a
    // friendly error before the round trip.
    if (form.latitude === null || form.longitude === null) {
      toast.error(coordsError || "GPS coordinates are required.");
      return;
    }
    if (coordsError) {
      toast.error(coordsError);
      return;
    }
    setBusy(true);
    try {
      // For safety-only projects, send an empty template list so the
      // backend doesn't try to merge stale selections from a previous
      // project_type pick.
      const base = isPano ? form : { ...form, capture_method: null };
      const payload = isSafetyOnly ? { ...base, template_ids: [] } : base;
      const { data } = await api.post("/projects", payload);
      toast.success("Project created");
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create project");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.3em] uppercase text-slate-400 font-display">New</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="new-project-heading">Create Project</h1>
      </div>

      <form onSubmit={submit} className="space-y-4 max-w-3xl" data-testid="new-project-form">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-300">Customer *</Label>
                <Input required value={form.customer} onChange={(e) => setField("customer", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="np-customer" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-300">Project Name *</Label>
                <Input required value={form.project_name} onChange={(e) => setField("project_name", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="np-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-300">Site Number *</Label>
                <Input required value={form.site_number} onChange={(e) => setField("site_number", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-700 font-mono" data-testid="np-site-number" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-300">Site Name *</Label>
                <Input required value={form.site_name} onChange={(e) => setField("site_name", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="np-site-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-300">Market</Label>
                <Input value={form.market} onChange={(e) => setField("market", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="np-market" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-300">Date</Label>
                <Input type="date" value={form.project_date} onChange={(e) => setField("project_date", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="np-date" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-300">
                  GPS Coordinates <span className="text-red-400">*</span>
                </Label>
                <div className="mt-1.5">
                  <Input
                    value={coordsInput}
                    onChange={(e) => handleCoordsChange(e.target.value)}
                    placeholder="35.123456, -97.654321"
                    className="bg-slate-950 border-slate-700 font-mono"
                    inputMode="decimal"
                    data-testid="np-coords"
                    required
                  />
                  <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                    <Crosshair className="w-3 h-3" /> Decimal degrees. Latitude first, then longitude — e.g. <span className="font-mono text-slate-300">35.123456, -97.654321</span>.
                  </div>
                  {coordsError && (
                    <div className="text-[11px] text-red-400 mt-1" data-testid="np-coords-error">{coordsError}</div>
                  )}
                  {!coordsError && form.latitude !== null && form.longitude !== null && (
                    <div className="text-[11px] text-emerald-400 mt-1 font-mono" data-testid="np-coords-parsed">
                      Parsed: lat {form.latitude}, lon {form.longitude}
                    </div>
                  )}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-300">
                  Address <span className="text-slate-500 font-normal normal-case tracking-normal ml-1">(optional)</span>
                </Label>
                <Textarea
                  value={form.address}
                  onChange={(e) => setField("address", e.target.value)}
                  placeholder="123 Main St, City, State ZIP"
                  className="mt-1.5 bg-slate-950 border-slate-700"
                  rows={2}
                  data-testid="np-address"
                />
                <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Postal address for reference — crew navigation always uses the GPS coords above.
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-300">Foreman</Label>
                <Input value={form.foreman} onChange={(e) => setField("foreman", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="np-foreman" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-300">Project Type *</Label>
                <Select value={form.project_type} onValueChange={(v) => setField("project_type", v)}>
                  <SelectTrigger className="mt-1.5 bg-slate-950 border-slate-700" data-testid="np-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isPano && (
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-300">
                    Drone or Climb <span className="text-red-400">*</span>
                  </Label>
                  <div className="text-[11px] text-slate-500 mt-0.5 mb-2">
                    The chosen section becomes required on the Photos tab; the other one is hidden.
                  </div>
                  <CaptureMethodPicker value={form.capture_method} onChange={(v) => setField("capture_method", v)} testid="np-capture-method" />
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-slate-800">
              {isSafetyOnly ? (
                <div
                  className="text-xs text-slate-400 italic flex items-start gap-2 p-3 rounded-md bg-slate-950 border border-slate-800"
                  data-testid="np-safety-only-notice"
                >
                  <FileStack className="w-3.5 h-3.5 text-slate-500 mt-0.5" />
                  <div>
                    <span className="text-slate-300 font-display tracking-wider uppercase text-[10px] block mb-1">Safety-Only Project</span>
                    No photo templates needed — this project will only capture the daily Tailgate and JSA paperwork.
                  </div>
                </div>
              ) : (
                <>
                  <Label className="text-xs uppercase tracking-wide text-slate-300 flex items-center gap-2">
                    <FileStack className="w-3.5 h-3.5 text-blue-400" /> Photo Templates *
                  </Label>
                  <div className="text-[11px] text-slate-500 mt-0.5 mb-2">
                    Pick at least one template. Categories &amp; items from selected templates are merged (deduped by file name). The project will only require the photos defined in the templates you select here.
                  </div>
                  {templates.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No templates available. Admin → Templates to create.</div>
                  ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                  {templates.map((t) => {
                    const checked = form.template_ids.includes(t.id);
                    const itemCount = (t.categories || []).reduce((s, c) => s + (c.items || []).length, 0);
                    const matchesType = !t.project_type || t.project_type === form.project_type;
                    return (
                      <label
                        key={t.id}
                        className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer transition-colors ${
                          checked
                            ? "bg-blue-950/30 border-blue-700"
                            : matchesType
                              ? "bg-slate-950 border-slate-700 hover:border-slate-600"
                              : "bg-slate-950/60 border-slate-800 opacity-70 hover:opacity-100"
                        }`}
                        data-testid={`np-tpl-${t.id}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggleTemplate(t.id)} className="mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{t.name}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5 font-mono truncate">
                            {t.project_type || "any type"}
                            {t.customer ? ` · ${t.customer}` : ""}
                            {t.is_default ? " · default" : ""}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {t.categories?.length || 0} categories · {itemCount} items
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
                  <SharedSlotsPreview templateIds={form.template_ids} testid="np-shared-slots" />
                </>
              )}
            </div>

            <div className="pt-2">
              <Label className="text-xs uppercase tracking-wide text-slate-300">Crew Members</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  placeholder="Name"
                  value={newCrew}
                  onChange={(e) => setNewCrew(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCrew(); } }}
                  className="bg-slate-950 border-slate-700"
                  data-testid="np-crew-input"
                />
                <Button type="button" onClick={addCrew} className="bg-blue-700 hover:bg-blue-600" data-testid="np-crew-add">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.crew_members.map((c, i) => (
                  // Crew rows don't have a server-side id (they're free-form
                  // local-only state), so we compose a stable key from the
                  // name+role+index — collisions only happen if a user
                  // intentionally adds two identical crew rows, which we
                  // disambiguate with the position index.
                  <span key={`${c.name}-${c.role || ""}-${i}`} className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700 text-sm" data-testid={`np-crew-chip-${i}`}>
                    {c.name}
                    <button type="button" onClick={() => removeCrew(i)} className="text-slate-400 hover:text-red-400"><X className="w-3 h-3"/></button>
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="np-cancel">Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-red-700 hover:bg-red-600 font-display tracking-wider" data-testid="np-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            CREATE PROJECT
          </Button>
        </div>
      </form>
    </AppShell>
  );
}
